import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import {useAuth} from "../auth";
import {chatSummaries} from "../api/orderChat";
import type {ChatSummary} from "../api/orderChat";
import {CHAT_REFRESH_MS, OrderChat} from "./OrderChat";
import {useRoute} from "../hooks";

type ChatPlacement = "drawer" | "history" | "standalone";
interface OrderChatPanelContextValue { orderID: string | null; placement: ChatPlacement; openChat: (orderID: string, placement?: ChatPlacement) => void; closeChat: () => void; }
const OrderChatPanelContext = createContext<OrderChatPanelContextValue | null>(null);
const NOOP_ORDER_CHAT_PANEL: OrderChatPanelContextValue = {orderID: null, placement: "standalone", openChat: () => undefined, closeChat: () => undefined};

export function useOrderChatPanel(): OrderChatPanelContextValue {
    const value = useContext(OrderChatPanelContext);
    return value ?? NOOP_ORDER_CHAT_PANEL;
}

export function OrderChatProvider({children}: {children: React.ReactNode}): JSX.Element {
    const {token} = useAuth();
    const [orderID, setOrderID] = useState<string | null>(null);
    const [placement, setPlacement] = useState<ChatPlacement>("standalone");
    const route = useRoute();
    const actions = useMemo(() => ({openChat: (id: string, nextPlacement: ChatPlacement = "standalone") => { setOrderID(id); setPlacement(nextPlacement); }, closeChat: () => setOrderID(null)}), []);
    const value = useMemo(() => ({...actions, orderID, placement}), [actions, orderID, placement]);
    useEffect(() => {
        const requested = new URLSearchParams(route.split("?")[1] || "").get("chat");
        if (requested && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(requested)) {
            actions.openChat(requested);
        } else actions.closeChat();
    }, [route, token, actions]);
    useEffect(() => {
        if (!orderID) return;
        const onKey = (event: KeyboardEvent): void => { if (event.key === "Escape") actions.closeChat(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [orderID, actions]);
    return <OrderChatPanelContext.Provider value={value}>
        {children}
        {token && orderID && <aside className={`order-chat-inline-panel placement-${placement}`} aria-label="Чат по заявке">
            <header className="order-chat-inline-header"><strong>Чат по заявке <span className="mono">{orderID}</span></strong><button className="details-close" type="button" onClick={value.closeChat} aria-label="Закрыть чат">×</button></header>
            <div className="order-chat-inline-scroll"><OrderChat key={`${token}:${orderID}`} token={token} orderID={orderID}/></div>
        </aside>}
    </OrderChatPanelContext.Provider>;
}

export function OrderChatCenter(): JSX.Element | null {
    const {token} = useAuth();
    const {openChat} = useOrderChatPanel();
    const [summaries, setSummaries] = useState<ChatSummary[]>([]);
    const [error, setError] = useState(false);
    useEffect(() => {
        setSummaries([]);
        if (!token) return;
        let disposed = false; let reading = false;
        const refresh = async (): Promise<void> => {
            if (disposed || reading || document.visibilityState === "hidden" || !navigator.onLine) return;
            reading = true;
            try { const response = await chatSummaries(token); if (!disposed) { setSummaries(response.items || []); setError(false); } }
            catch { if (!disposed) { setSummaries([]); setError(true); } }
            finally { reading = false; }
        };
        const wake = (): void => { void refresh(); }; wake();
        const timer = window.setInterval(wake, CHAT_REFRESH_MS);
        window.addEventListener("focus", wake); window.addEventListener("online", wake); document.addEventListener("visibilitychange", wake);
        return () => { disposed = true; window.clearInterval(timer); window.removeEventListener("focus", wake); window.removeEventListener("online", wake); document.removeEventListener("visibilitychange", wake); };
    }, [token]);
    if (!token) return null;
    const unread = summaries.filter(item => item.unread_count > 0);
    return <aside className="order-chat-notifications" aria-label="Новые сообщения">
        {error && <span role="status">Уведомления чата временно недоступны</span>}
        {unread.length > 0 && <details><summary>Новые сообщения: {unread.reduce((sum, item) => sum + item.unread_count, 0)}</summary><ul>{unread.map(item => <li key={item.order_id}><button className="order-chat-notification-link" type="button" onClick={() => openChat(item.order_id)}>{item.remote_id} · {item.unread_count}</button></li>)}</ul></details>}
    </aside>;
}
